"use client";

import { forwardRef, type ButtonHTMLAttributes, type ReactElement, cloneElement, isValidElement } from "react";

type Variant = "primary" | "secondary" | "ghost" | "link";
type Size = "sm" | "md" | "lg";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  asChild?: boolean;
};

const sizeClasses: Record<Size, string> = {
  sm: "h-9 px-4 text-sm",
  md: "h-11 px-5 text-sm",
  lg: "h-14 px-7 text-base",
};

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-[var(--color-button-primary-bg)] text-[var(--color-button-primary-text)] hover:bg-[var(--color-button-primary-pressed)] focus-visible:outline-[var(--color-text-primary)]",
  secondary:
    "bg-transparent text-[var(--color-text-primary)] border border-[var(--color-surface-border-strong)] hover:border-[var(--color-accent-base)] hover:text-[var(--color-accent-base)]",
  ghost:
    "bg-transparent text-[var(--color-text-primary)] hover:bg-[var(--color-surface-glass)]",
  link:
    "bg-transparent text-[var(--color-accent-base)] underline underline-offset-4 hover:text-[var(--color-accent-strong)] h-auto px-0",
};

const base =
  "inline-flex items-center justify-center gap-2 rounded-full font-semibold tracking-tight transition-all duration-200 motion-reduce:transition-none disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2";

/**
 * Themed button. `asChild` forwards the styling onto a nested element
 * (typically `<a>`) so anchors and Next.js `<Link>` get the same visuals
 * without extra wrappers.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", asChild = false, className = "", children, ...rest },
  ref
) {
  const cls = `${base} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`.trim();

  if (asChild && isValidElement(children)) {
    const child = children as ReactElement<{ className?: string }>;
    return cloneElement(child, {
      className: `${cls} ${child.props.className ?? ""}`.trim(),
    });
  }

  return (
    <button ref={ref} className={cls} {...rest}>
      {children}
    </button>
  );
});
